import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Maximize2, X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

type Status = "covered" | "not-covered" | "investigational";

type Payor = {
  payor: string;
  policy: string;
  esg: Status;
  esgNote: string;
  balloon: Status;
  balloonNote: string;
};

const PAYORS: Payor[] = [
  {
    payor: "Anthem BCBS of Virginia / HealthKeepers",
    policy: "CG-SURG-83",
    esg: "covered",
    esgNote: "Covered under the same BMI criteria used for sleeve gastrectomy",
    balloon: "not-covered",
    balloonNote: "Not covered",
  },
  {
    payor: "Cigna",
    policy: "Coverage Policy 0051",
    esg: "covered",
    esgNote: "Covered as medically necessary at BMI 35+, or 30–34.9 with one condition",
    balloon: "not-covered",
    balloonNote: "Not covered",
  },
  {
    payor: "Aetna",
    policy: "CPB 0157",
    esg: "investigational",
    esgNote: "Considered experimental and investigational",
    balloon: "investigational",
    balloonNote: "Considered experimental and investigational",
  },
  {
    payor: "UnitedHealthcare",
    policy: "Bariatric Surgery medical policy",
    esg: "investigational",
    esgNote: "Considered unproven and not medically necessary",
    balloon: "investigational",
    balloonNote: "Considered unproven and not medically necessary",
  },
  {
    payor: "Medicare",
    policy: "NCD 100.1",
    esg: "not-covered",
    esgNote: "No national coverage for ESG",
    balloon: "not-covered",
    balloonNote: "No national coverage for the balloon",
  },
  {
    payor: "Virginia Medicaid (Cardinal Care)",
    policy: "Managed-care plan review, prior authorization required",
    esg: "not-covered",
    esgNote: "Generally not covered by the managed-care plans",
    balloon: "not-covered",
    balloonNote: "Generally not covered by the managed-care plans",
  },
];

const STATUS_LABEL: Record<Status, string> = {
  covered: "Often covered",
  "not-covered": "Not covered",
  investigational: "Investigational",
};

const STATUS_CLASS: Record<Status, string> = {
  covered: "border-brand-logo/40 bg-brand-logo/10 text-brand-mid",
  "not-covered": "border-border bg-muted text-muted-foreground",
  investigational: "border-border bg-muted/60 text-brand-mid",
};

const CAPTION = (
  <>
    Coverage summary for endoscopic sleeve gastroplasty (CPT 43889, permanent code effective
    January 1, 2026) and the intragastric balloon, by payor. Policies change often and employer
    plans can differ from the payor&rsquo;s general policy, so we verify your individual benefits
    before scheduling.
  </>
);

function StatusCell({
  status,
  note,
  expanded,
}: {
  status: Status;
  note: string;
  expanded?: boolean;
}) {
  return (
    <TableCell className={`align-top ${expanded ? "p-4" : ""}`}>
      <span
        className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASS[status]}`}
      >
        {STATUS_LABEL[status]}
      </span>
      <span
        className={`mt-1 block leading-[1.5] text-muted-foreground ${expanded ? "text-base" : "text-sm"}`}
      >
        {note}
      </span>
    </TableCell>
  );
}

function CoverageSkeleton() {
  return (
    <ul className="divide-y divide-border" aria-hidden="true">
      {PAYORS.map((p) => (
        <li key={p.payor} className="space-y-3 p-4">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-5 w-28 rounded-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-5 w-28 rounded-full" />
          <Skeleton className="h-3 w-4/5" />
        </li>
      ))}
    </ul>
  );
}

function CoverageCards({ expanded = false }: { expanded?: boolean }) {
  return (
    <ul
      className="divide-y divide-border"
      aria-label="ESG and intragastric balloon coverage by payor"
    >
      {PAYORS.map((p) => (
        <li key={p.payor} className={expanded ? "p-4" : "p-3"}>
          <h4 className="text-[0.95rem] font-medium leading-[1.35] text-brand-mid">
            {p.payor}
          </h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="sr-only">Policy: </span>
            {p.policy}
          </p>
          <dl className="mt-3 space-y-3">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                ESG
              </dt>
              <dd className="mt-1">
                <span
                  className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASS[p.esg]}`}
                >
                  {STATUS_LABEL[p.esg]}
                </span>
                <span className="mt-1 block text-sm leading-[1.5] text-muted-foreground">
                  {p.esgNote}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Intragastric balloon
              </dt>
              <dd className="mt-1">
                <span
                  className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASS[p.balloon]}`}
                >
                  {STATUS_LABEL[p.balloon]}
                </span>
                <span className="mt-1 block text-sm leading-[1.5] text-muted-foreground">
                  {p.balloonNote}
                </span>
              </dd>
            </div>
          </dl>
        </li>
      ))}
    </ul>
  );
}

function CoverageTable({ expanded = false }: { expanded?: boolean }) {
  return (
    <Table className={expanded ? "min-w-[60rem] text-base" : undefined}>
      <TableCaption className="px-4 pb-4 text-left text-sm">{CAPTION}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col" className={expanded ? "min-w-[16rem] p-4" : "min-w-[13rem]"}>
            Payor
          </TableHead>
          <TableHead scope="col" className={expanded ? "min-w-[14rem] p-4" : "min-w-[11rem]"}>
            Policy number
          </TableHead>
          <TableHead scope="col" className={expanded ? "min-w-[18rem] p-4" : "min-w-[15rem]"}>
            ESG
          </TableHead>
          <TableHead scope="col" className={expanded ? "min-w-[18rem] p-4" : "min-w-[15rem]"}>
            Intragastric balloon
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {PAYORS.map((p) => (
          <TableRow key={p.payor}>
            <TableHead
              scope="row"
              className={`align-top font-medium text-brand-mid ${expanded ? "p-4" : ""}`}
            >
              {p.payor}
            </TableHead>
            <TableCell
              className={`align-top text-muted-foreground ${expanded ? "p-4 text-base" : "text-sm"}`}
            >
              {p.policy}
            </TableCell>
            <StatusCell status={p.esg} note={p.esgNote} expanded={expanded} />
            <StatusCell status={p.balloon} note={p.balloonNote} expanded={expanded} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function EsgBalloonCoverageTable() {
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);
  const expandButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!expanded) {
      setReady(false);
      return;
    }
    const t = window.setTimeout(() => setReady(true), 180);
    return () => window.clearTimeout(t);
  }, [expanded]);

  return (
    <figure className="my-6 not-prose">
      <div className="relative">
        <button
          type="button"
          ref={expandButtonRef}
          onClick={() => setExpanded(true)}
          className="absolute -top-3 -right-3 z-10 inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-full border border-border bg-background p-2 text-xs font-medium text-foreground shadow-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2"
          aria-label="Expand insurance coverage table to full width"
        >
          <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">Expand</span>
        </button>
        <div className="rounded-xl border border-border md:overflow-x-auto">
          <div className="md:hidden">
            <CoverageCards />
            <p className="border-t border-border px-3 py-3 text-sm leading-[1.5] text-muted-foreground">
              {CAPTION}
            </p>
          </div>
          <div className="hidden md:block">
            <CoverageTable />
          </div>
        </div>
      </div>

      <DialogPrimitive.Root open={expanded} onOpenChange={setExpanded}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
          <DialogPrimitive.Content
            aria-modal="true"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              requestAnimationFrame(() => expandButtonRef.current?.focus());
            }}
            className="fixed inset-0 z-50 flex h-dvh flex-col duration-200 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:slide-out-to-bottom-4 md:overflow-auto md:p-4 md:data-[state=open]:zoom-in-95 md:data-[state=closed]:zoom-out-95 md:data-[state=open]:slide-in-from-bottom-0"
          >
            <div className="mx-auto flex h-full w-full max-w-[90rem] flex-col overflow-hidden border-border bg-background shadow-2xl md:h-auto md:rounded-lg md:border">
              <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-background px-4 py-3 md:static md:border-0 md:px-8 md:pt-8 md:pb-4">
                <DialogPrimitive.Title className="min-w-0 text-base font-semibold text-foreground md:text-xl">
                  ESG and intragastric balloon coverage by payor
                </DialogPrimitive.Title>
                <DialogPrimitive.Close
                  aria-label="Close coverage table (press Escape)"
                  className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-deep focus:ring-offset-2">
                  <X className="h-4 w-4" aria-hidden="true" />
                  Close
                </DialogPrimitive.Close>
              </div>
              <DialogPrimitive.Description
                className="sr-only"
              >
                Full-screen coverage table. Press Escape or use the Close button to return to the
                page.
              </DialogPrimitive.Description>
              <div
                tabIndex={0}
                role="region"
                aria-label="Coverage table content, scrollable"
                className="min-h-0 flex-1 overflow-auto overscroll-contain px-0 pb-6 md:px-8">
                <div className="md:hidden" aria-busy={!ready}>
                  {!ready ? (
                    <CoverageSkeleton />
                  ) : (
                    <div className="animate-fade-in">
                      <CoverageCards expanded />
                      <p className="border-t border-border px-4 py-4 text-sm leading-[1.5] text-muted-foreground">
                        {CAPTION}
                      </p>
                    </div>
                  )}
                </div>
                <div className="hidden md:block" aria-busy={!ready}>
                  {!ready ? (
                    <CoverageSkeleton />
                  ) : (
                    <div className="animate-fade-in">
                      <CoverageTable expanded />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

    </figure>
  );
}
